/**
 * PRELOAD (owner B1). Implements PreloadAPI (contracts/ui.ts).
 *
 *   preload.start()         idempotent; also the retry path after an error
 *   await preload.essential  every essential asset is in memory; the map may be created
 *   preload.loadDeferred()  after the user enters: the DEM, then "dem:ready"
 *   await preload.dem
 *
 * How the bytes arrive:
 *  1. GET /manifest.json (cache: "no-cache"). The essential set is every asset with essential:true;
 *     total = Σ manifest bytes. Content-Length is never used for progress: the CDN may compress JSON,
 *     and a stream reader counts decoded bytes, which is what the manifest records.
 *  2. Each part is looked up in Cache Storage first ("ld-assets-<manifest.version>", then any older
 *     "ld-assets-*" cache — part URLs are content-hashed, so any cached copy is valid and is carried
 *     over). Otherwise it is fetched with a ReadableStream reader, at most 4 parts at a time, and
 *     written to the current cache when complete. public/sw.js reads the same caches but never
 *     writes /a/*, so the bytes are stored once.
 *  3. Parts are joined in order into one ArrayBuffer per asset and checked against its sha256.
 *     A mismatch drops the cached copies and refetches once from the network before failing.
 *  4. basemap → registerMemoryArchive("basemap"); the JSON assets → actions.setData(); then
 *     `essential` resolves and "preload:essential" is emitted.
 *
 * The warm set (preload.warm(items), before start()): what the map asks for the moment it starts (the
 * MapLibre worker, the opening view's glyph ranges; the list lives in src/app). Each is fetched alongside
 * the packs, outside the part limit, so the HTTP cache (or the service worker) holds it when the map is
 * created; its bytes join the total when its headers state an uncompressed size in time, and `essential`
 * waits until every item has settled. A failed item never fails the essential set: the map fetches it
 * itself (L2-11).
 *
 * Progress is byte-accurate and monotonic: every received chunk is credited to its part, the shown
 * value is max(shown, Σ credited) clamped to the total, and it is published at most once per
 * animation frame (store + bus "preload:progress"), with a final synchronous flush at 100 %.
 *
 * Errors are calm: actions.setLoadError(<copy line>) (loader.offline when the browser is offline,
 * loader.error otherwise; loader.webgl when WebGL 2 is missing, which no retry can fix). start()
 * again resumes: parts already in memory or in Cache Storage are not fetched again. A part that
 * stops receiving bytes for 20 s is aborted and reported as an error.
 */
import type { PreloadAPI } from "@/contracts/ui";
import type { AssetEntry, AssetManifest } from "@/contracts/assets";
import type { ContourCollection, CorridorCollection, Dataset, RoutesFile, SiteCollection } from "@/contracts/data";
import type { GeoAssets } from "@/contracts/store";
import { actions, getApp } from "@/store/store";
import { bus } from "@/store/bus";
import { ticker } from "@/lib/ticker";
import { registerMemoryArchive } from "@/map/memorySource";
import { LOADER_COPY } from "./copy";

/* ─── Types ──────────────────────────────────────────────────────────────── */

export type FetchFn = (url: string, init?: RequestInit) => Promise<Response>;

/** A resource the map asks for as soon as it starts (the warm set, L2-11). */
export interface WarmItem {
  url: string;
  /** Called once its bytes are in (the HTTP cache or the service worker holds them). */
  onReady?: () => void;
}

export interface EssentialData {
  data: Dataset;
  routes: RoutesFile;
  geo: GeoAssets;
}

/** Where the preloader reports. The default wires it to the store and the bus. */
export interface PreloadSink {
  progress(loaded: number, total: number, asset?: string): void;
  error(message: string | null): void;
  data(d: EssentialData): void;
  essential(): void;
  /** Deferred set (the DEM). Not written to the store: the loader is gone by then. */
  deferredProgress?(loaded: number, total: number, asset: string): void;
  demReady(): void;
  /** A deferred asset failed (§F error.generic). Called once per session; retries continue quietly. */
  deferredError?(message: string): void;
}

export interface PreloadDeps {
  fetch: FetchFn;
  /** Cache Storage, or null where it is unavailable (insecure context, tests). */
  caches: CacheStorage | null;
  /** Web Crypto for sha256, or null (insecure context): the check is then skipped with a warning. */
  subtle: SubtleCrypto | null;
  /** Runs `cb` on the next animation frame (progress throttle). */
  schedule(cb: () => void): void;
  sink: PreloadSink;
  registerArchive(name: string, buf: ArrayBuffer): void;
  /** False when WebGL 2 is missing: nothing is downloaded. */
  webgl2?: () => boolean;
  /** Is the browser offline right now? Chooses the calm offline line. */
  offline?: () => boolean;
  /** Best-effort persistent storage, called once after the essential set. */
  persist?: () => void;
  warn?: (...a: unknown[]) => void;
  /** Parallel part downloads. Default 4. */
  parallel?: number;
  /** Abort a part after this long without a byte. Default 20 000 ms. */
  stallMs?: number;
  /** Give up on /manifest.json after this long. Default 15 000 ms. */
  manifestTimeoutMs?: number;
  /** After the manifest, wait at most this long for the warm set's headers before fixing the total. Default 1000 ms. */
  warmHeadersMs?: number;
  manifestUrl?: string;
}

export type PreloadState = "idle" | "loading" | "ready" | "error" | "unsupported";

export interface PreloadStatus {
  state: PreloadState;
  dem: "idle" | "loading" | "ready" | "error";
  loaded: number;
  total: number;
  version: string | null;
  /** Technical detail of the last failure (the store only carries the calm copy line). */
  lastError: string | null;
  /** Where each finished part came from. */
  sources: Record<string, "cache" | "network">;
  /** The warm set: each item's state, and whether its bytes are in the total. */
  warm: Record<string, { state: "loading" | "ready" | "failed"; counted: boolean }>;
}

export interface Preloader extends PreloadAPI {
  status(): PreloadStatus;
  /** Registers the warm set; call before the first start() (later calls are ignored). */
  warm(items: readonly WarmItem[]): void;
}

/* ─── Small helpers (exported for tests) ─────────────────────────────────── */

export const CACHE_PREFIX = "ld-assets-";
export const cacheNameFor = (version: string): string => `${CACHE_PREFIX}${version}`;

class LoadError extends Error {
  constructor(
    message: string,
    readonly kind: "network" | "http" | "integrity" | "format" | "stall" | "manifest",
  ) {
    super(message);
    this.name = "LoadError";
  }
}

const isAbort = (e: unknown): boolean => e instanceof DOMException && e.name === "AbortError";

/** Join ArrayBuffers in order into one. */
export function concatBuffers(parts: readonly ArrayBuffer[]): ArrayBuffer {
  if (parts.length === 1) return parts[0];
  let n = 0;
  for (const p of parts) n += p.byteLength;
  const out = new Uint8Array(n);
  let o = 0;
  for (const p of parts) {
    out.set(new Uint8Array(p), o);
    o += p.byteLength;
  }
  return out.buffer;
}

export async function sha256Hex(subtle: SubtleCrypto, buf: ArrayBuffer): Promise<string> {
  const d = new Uint8Array(await subtle.digest("SHA-256", buf));
  let s = "";
  for (const b of d) s += b.toString(16).padStart(2, "0");
  return s;
}

/** A FIFO semaphore: at most `n` holders at once. */
export function semaphore(n: number): <T>(fn: () => Promise<T>) => Promise<T> {
  let active = 0;
  const queue: Array<() => void> = [];
  const next = () => {
    if (active >= n) return;
    const go = queue.shift();
    if (go) {
      active++;
      go();
    }
  };
  return <T>(fn: () => Promise<T>) =>
    new Promise<T>((resolve, reject) => {
      queue.push(() => {
        fn()
          .then(resolve, reject)
          .finally(() => {
            active--;
            next();
          });
      });
      next();
    });
}

/**
 * Monotonic, byte-accurate progress. Chunks are credited per part; a part that restarts (retry)
 * drops its credit, but the published value never moves backwards.
 */
export class ProgressMeter {
  private credit = new Map<string, number>();
  private sum = 0;
  private shown = 0;
  total = 0;
  add(part: string, bytes: number): void {
    this.credit.set(part, (this.credit.get(part) ?? 0) + bytes);
    this.sum += bytes;
  }
  reset(part: string): void {
    this.sum -= this.credit.get(part) ?? 0;
    this.credit.delete(part);
  }
  /** Published value: max(previous, Σ credited), clamped to the total. */
  value(): number {
    const v = Math.min(this.total, Math.max(this.shown, this.sum));
    this.shown = v;
    return v;
  }
  complete(): number {
    this.shown = this.total;
    return this.total;
  }
}

function manifestShape(m: unknown): m is AssetManifest {
  if (!m || typeof m !== "object") return false;
  const o = m as Partial<AssetManifest>;
  return typeof o.version === "string" && !!o.assets && typeof o.assets === "object";
}

const REQUIRED = ["basemap", "places", "routes", "site", "roads", "future", "contours"] as const;

/* ─── The preloader ──────────────────────────────────────────────────────── */

export function createPreloader(deps: PreloadDeps): Preloader {
  const parallel = Math.max(1, deps.parallel ?? 4);
  const stallMs = deps.stallMs ?? 20_000;
  const manifestTimeoutMs = deps.manifestTimeoutMs ?? 15_000;
  const warmHeadersMs = deps.warmHeadersMs ?? 1000;
  const manifestUrl = deps.manifestUrl ?? "/manifest.json";
  const warn = deps.warn ?? ((...a: unknown[]) => console.warn("[preload]", ...a));
  const limit = semaphore(parallel);

  let resolveEssential!: () => void;
  let resolveDem!: () => void;
  const essential = new Promise<void>((r) => (resolveEssential = r));
  const dem = new Promise<void>((r) => (resolveDem = r));

  let state: PreloadState = "idle";
  let demState: PreloadStatus["dem"] = "idle";
  let manifest: AssetManifest | null = null;
  let lastError: string | null = null;
  let persisted = false;
  const sources: Record<string, "cache" | "network"> = {};

  /** Finished part bytes kept across retries (dropped once their asset is assembled). */
  const partBufs = new Map<string, ArrayBuffer>();
  /** Assembled, verified asset bytes kept across retries (dropped after essential). */
  const assetBufs = new Map<string, ArrayBuffer>();

  const meter = new ProgressMeter();
  const demMeter = new ProgressMeter();
  let lastAsset: string | undefined;
  let flushPending = false;
  /** The run's total is fixed (the manifest is in and the warm set sized); progress is published from then on. */
  let totalKnown = false;

  function flushNow(): void {
    flushPending = false;
    deps.sink.progress(meter.value(), meter.total, lastAsset);
  }
  function scheduleFlush(): void {
    if (flushPending) return;
    flushPending = true;
    deps.schedule(() => {
      if (!flushPending) return;
      flushPending = false;
      if (state === "loading" && totalKnown) flushNow();
    });
  }
  let demFlushPending = false;
  function scheduleDemFlush(): void {
    if (demFlushPending) return;
    demFlushPending = true;
    deps.schedule(() => {
      demFlushPending = false;
      if (demState === "loading") deps.sink.deferredProgress?.(demMeter.value(), demMeter.total, "dem");
    });
  }

  /* ── Cache Storage ── */

  let cachePromise: Promise<Cache | null> | null = null;
  function currentCache(version: string): Promise<Cache | null> {
    if (!deps.caches) return Promise.resolve(null);
    cachePromise ??= deps.caches.open(cacheNameFor(version)).catch((e) => {
      warn("Cache Storage unavailable", e);
      return null;
    });
    return cachePromise;
  }
  const pendingWrites = new Set<Promise<void>>();

  async function fromCache(version: string, url: string): Promise<ArrayBuffer | null> {
    const cache = await currentCache(version);
    if (!cache || !deps.caches) return null;
    try {
      let hit = await cache.match(url);
      let carried = false;
      if (!hit) {
        // An older manifest version may already hold this (content-hashed) part.
        hit = await deps.caches.match(url);
        carried = !!hit;
      }
      if (!hit || !hit.ok) return null;
      const buf = await hit.arrayBuffer();
      if (carried) writeCache(cache, url, buf, hit.headers.get("content-type"));
      return buf;
    } catch (e) {
      warn("cache read failed", url, e);
      return null;
    }
  }

  function writeCache(cache: Cache, url: string, buf: ArrayBuffer, type: string | null): void {
    // The Response constructor copies the bytes, so `buf` stays ours.
    const res = new Response(buf, {
      status: 200,
      headers: { "Content-Type": type || "application/octet-stream", "Content-Length": String(buf.byteLength) },
    });
    const p = cache
      .put(url, res)
      .catch((e) => warn("cache write failed (storage full?)", url, e))
      .finally(() => pendingWrites.delete(p));
    pendingWrites.add(p);
  }

  async function dropCached(url: string): Promise<void> {
    if (!deps.caches) return;
    try {
      for (const name of await deps.caches.keys()) {
        if (name.startsWith(CACHE_PREFIX)) await (await deps.caches.open(name)).delete(url);
      }
    } catch (e) {
      warn("cache delete failed", url, e);
    }
  }

  async function pruneOldCaches(version: string): Promise<void> {
    if (!deps.caches) return;
    await Promise.allSettled([...pendingWrites]);
    try {
      const keep = cacheNameFor(version);
      for (const name of await deps.caches.keys()) {
        if (name.startsWith(CACHE_PREFIX) && name !== keep) await deps.caches.delete(name);
      }
    } catch (e) {
      warn("cache prune failed", e);
    }
  }

  /* ── Network ── */

  /** Streams one URL: onChunk(n) for every n bytes received, onHeaders(res) once the response is in. */
  async function streamPart(url: string, signal: AbortSignal, onChunk: (n: number) => void, onHeaders?: (res: Response) => void): Promise<{ buf: ArrayBuffer; type: string | null }> {
    const ac = new AbortController();
    const abort = () => ac.abort();
    signal.addEventListener("abort", abort, { once: true });
    let stalled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const arm = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        stalled = true;
        ac.abort();
      }, stallMs);
    };
    try {
      arm();
      let res: Response;
      try {
        res = await deps.fetch(url, { signal: ac.signal, credentials: "same-origin" });
      } catch (e) {
        if (stalled) throw new LoadError(`${url}: no response for ${stallMs} ms`, "stall");
        if (isAbort(e)) throw e;
        throw new LoadError(`${url}: ${(e as Error)?.message ?? e}`, "network");
      }
      if (!res.ok) throw new LoadError(`${url}: HTTP ${res.status}`, "http");
      const type = res.headers.get("content-type");
      // A dev server or SPA fallback answers a missing file with index.html and 200.
      if (type && /text\/html/i.test(type)) throw new LoadError(`${url}: got HTML instead of the asset (missing file?)`, "http");
      onHeaders?.(res);
      if (!res.body) {
        const buf = await res.arrayBuffer();
        onChunk(buf.byteLength);
        return { buf, type };
      }
      const reader = res.body.getReader();
      const chunks: Uint8Array[] = [];
      let n = 0;
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          arm();
          chunks.push(value);
          n += value.byteLength;
          onChunk(value.byteLength);
        }
      } catch (e) {
        if (stalled) throw new LoadError(`${url}: stalled after ${n} bytes`, "stall");
        if (isAbort(e) || signal.aborted) throw e instanceof Error ? e : new DOMException("Aborted", "AbortError");
        throw new LoadError(`${url}: connection lost after ${n} bytes`, "network");
      }
      const out = new Uint8Array(n);
      let o = 0;
      for (const c of chunks) {
        out.set(c, o);
        o += c.byteLength;
      }
      return { buf: out.buffer, type };
    } finally {
      clearTimeout(timer);
      signal.removeEventListener("abort", abort);
    }
  }

  /** Credit keys are per attempt, so an aborted attempt that unwinds late cannot erase a retry's bytes. */
  let attemptSeq = 0;
  /** The credit key of each part's finished attempt (to un-credit a part dropped by the checksum). */
  const creditKey = new Map<string, string>();

  /** One part: memory → Cache Storage → network (credited to `m` as it streams). */
  async function getPart(version: string, url: string, m: ProgressMeter, onBytes: () => void, signal: AbortSignal, allowCache: boolean): Promise<ArrayBuffer> {
    const mem = partBufs.get(url);
    if (mem) return mem;
    if (allowCache) {
      const cached = await fromCache(version, url);
      if (cached) {
        if (signal.aborted) throw new DOMException("Aborted", "AbortError");
        const key = `${url}#${++attemptSeq}`;
        m.add(key, cached.byteLength);
        creditKey.set(url, key);
        onBytes();
        partBufs.set(url, cached);
        sources[url] = "cache";
        return cached;
      }
    }
    return limit(async () => {
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      // A retried part starts again from zero; the meter never shows less than it did.
      const key = `${url}#${++attemptSeq}`;
      try {
        const { buf, type } = await streamPart(url, signal, (n) => {
          m.add(key, n);
          onBytes();
        });
        creditKey.set(url, key);
        partBufs.set(url, buf);
        sources[url] = "network";
        const cache = await currentCache(version);
        if (cache) writeCache(cache, url, buf, type);
        return buf;
      } catch (e) {
        m.reset(key);
        throw e;
      }
    });
  }

  /** Every part of one asset, joined and verified. Cached copies that fail the check are refetched once. */
  async function loadAsset(version: string, a: AssetEntry, m: ProgressMeter, onBytes: () => void, signal: AbortSignal): Promise<ArrayBuffer> {
    const done = assetBufs.get(a.name);
    if (done) return done;
    for (let attempt = 0; attempt < 2; attempt++) {
      const parts = await Promise.all(a.parts.map((url) => getPart(version, url, m, onBytes, signal, attempt === 0)));
      const buf = concatBuffers(parts);
      const ok = await verify(a, buf);
      if (ok) {
        for (const url of a.parts) partBufs.delete(url);
        assetBufs.set(a.name, buf);
        return buf;
      }
      const fromCacheParts = a.parts.filter((u) => sources[u] === "cache");
      for (const url of a.parts) {
        partBufs.delete(url);
        const key = creditKey.get(url);
        if (key) m.reset(key);
        await dropCached(url);
      }
      if (attempt === 0 && fromCacheParts.length) {
        warn(`${a.name}: cached copy failed its checksum; fetching it again`);
        continue;
      }
      throw new LoadError(`${a.name}: checksum mismatch (${buf.byteLength} of ${a.bytes} bytes)`, "integrity");
    }
    throw new LoadError(`${a.name}: checksum mismatch`, "integrity");
  }

  let warnedNoCrypto = false;
  async function verify(a: AssetEntry, buf: ArrayBuffer): Promise<boolean> {
    if (buf.byteLength !== a.bytes) return false;
    if (!deps.subtle) {
      if (!warnedNoCrypto) warn("Web Crypto unavailable (insecure context?): checksums skipped");
      warnedNoCrypto = true;
      return true;
    }
    return (await sha256Hex(deps.subtle, buf)) === a.sha256.toLowerCase();
  }

  async function getManifest(): Promise<AssetManifest> {
    if (manifest) return manifest;
    const ac = new AbortController();
    const t = setTimeout(() => ac.abort(), manifestTimeoutMs);
    try {
      let res: Response;
      try {
        res = await deps.fetch(manifestUrl, { cache: "no-cache", signal: ac.signal, credentials: "same-origin" });
      } catch (e) {
        throw new LoadError(`manifest: ${ac.signal.aborted ? `no answer in ${manifestTimeoutMs} ms` : ((e as Error)?.message ?? e)}`, "network");
      }
      if (!res.ok) throw new LoadError(`manifest: HTTP ${res.status}`, "manifest");
      let m: unknown;
      try {
        m = await res.json();
      } catch {
        throw new LoadError("manifest: not JSON", "manifest");
      }
      if (!manifestShape(m)) throw new LoadError("manifest: unexpected shape", "manifest");
      for (const n of REQUIRED) {
        const a = m.assets[n];
        if (!a || !a.essential) throw new LoadError(`manifest: essential asset "${n}" is missing`, "manifest");
      }
      manifest = m;
      return m;
    } finally {
      clearTimeout(t);
    }
  }

  const text = new TextDecoder("utf-8");
  function parseJson<T>(name: string, buf: ArrayBuffer): T {
    try {
      return JSON.parse(text.decode(buf)) as T;
    } catch {
      throw new LoadError(`${name}: not valid JSON`, "format");
    }
  }

  function calmMessage(e: unknown): string {
    if (e instanceof LoadError && (e.kind === "network" || e.kind === "stall") && deps.offline?.()) return LOADER_COPY.offline;
    return LOADER_COPY.error;
  }

  /* ── Warm set (L2-11) ── */

  interface WarmRun {
    item: WarmItem;
    key: string;
    /** Decoded size, when the headers state it (identity encoding); null = unknown. */
    size: number | null;
    received: number;
    credited: number;
    /** Its bytes are in the total (decided once, when the total is fixed). */
    counted: boolean;
    state: "loading" | "ready" | "failed";
    headers: Promise<void>;
    done: Promise<void>;
  }
  let warmList: readonly WarmItem[] = [];
  let warmRuns: WarmRun[] | null = null;
  let warmFixed = false;

  /** Credits a counted item up to what it has received (all of it once it has settled). */
  function creditWarm(w: WarmRun, n: number): void {
    w.received += n;
    if (!w.counted || w.size === null) return;
    // A failed item is credited as settled: the map fetches it itself, and the rings still close.
    const want = w.state === "loading" ? Math.min(w.size, w.received) : w.size;
    if (want <= w.credited) return;
    meter.add(w.key, want - w.credited);
    w.credited = want;
    scheduleFlush();
  }

  /** Starts the warm set once per preloader (a retry does not fetch it again). */
  function startWarm(): WarmRun[] {
    if (warmRuns) return warmRuns;
    warmRuns = warmList.map((item) => {
      let headersIn!: () => void;
      const headers = new Promise<void>((r) => (headersIn = r));
      const w: WarmRun = { item, key: `warm:${item.url}`, size: null, received: 0, credited: 0, counted: false, state: "loading", headers, done: Promise.resolve() };
      const onHeaders = (res: Response) => {
        const encoding = (res.headers.get("content-encoding") ?? "identity").trim().toLowerCase();
        const len = Number(res.headers.get("content-length"));
        if (encoding === "identity" && Number.isFinite(len) && len > 0) w.size = len;
        headersIn();
      };
      w.done = streamPart(item.url, new AbortController().signal, (n) => creditWarm(w, n), onHeaders)
        .then(
          () => {
            w.state = "ready";
            creditWarm(w, 0);
            try {
              item.onReady?.();
            } catch (e) {
              warn("warm onReady failed", item.url, e);
            }
          },
          (e: unknown) => {
            w.state = "failed";
            creditWarm(w, 0);
            warn(`warm ${item.url} failed (the map will fetch it itself):`, e instanceof Error ? e.message : e);
          },
        )
        .finally(() => headersIn());
      return w;
    });
    return warmRuns;
  }

  /** Decides once which warm items join the total: those whose size is known when all headers are in, or after warmHeadersMs. */
  function fixWarm(runs: WarmRun[]): Promise<void> {
    if (warmFixed || !runs.length) {
      warmFixed = true;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      const finish = () => {
        clearTimeout(timer);
        if (!warmFixed) {
          warmFixed = true;
          for (const w of runs) {
            w.counted = w.size !== null && w.state !== "failed";
            creditWarm(w, 0);
          }
        }
        resolve();
      };
      const timer = setTimeout(finish, warmHeadersMs);
      void Promise.all(runs.map((w) => w.headers)).then(finish);
    });
  }

  /* ── Essential run ── */

  async function runEssential(): Promise<void> {
    const ac = new AbortController();
    totalKnown = false;
    const warmSet = startWarm(); // alongside the manifest request, so the small items are in early
    const m = await getManifest();
    const assets = Object.values(m.assets).filter((a) => a.essential);
    const essentialBytes = assets.reduce((s, a) => s + a.bytes, 0);
    // The packs start at once; the total (and so the progress) is published once the warm set is sized.
    const totalFixed = fixWarm(warmSet).then(() => {
      meter.total = essentialBytes + warmSet.reduce((s, w) => s + (w.counted && w.size !== null ? w.size : 0), 0);
      totalKnown = true;
      flushNow();
    });

    // Largest first, so the long pole (the basemap parts) takes the first download slots.
    const order = [...assets].sort((a, b) => b.bytes - a.bytes);
    const onBytes = () => scheduleFlush();
    const results = new Map<string, ArrayBuffer>();
    try {
      await Promise.all(
        order.map(async (a) => {
          const buf = await loadAsset(m.version, a, meter, () => {
            lastAsset = a.name;
            onBytes();
          }, ac.signal);
          results.set(a.name, buf);
        }),
      );
    } catch (e) {
      ac.abort(); // stop the other parts; finished ones stay in memory and in the cache
      throw e;
    }
    await totalFixed;
    // The warm set settles (it never rejects) before the map may be created, so the map finds it cached.
    await Promise.all(warmSet.map((w) => w.done));

    const essentialData: EssentialData = {
      data: parseJson<Dataset>("places", results.get("places")!),
      routes: parseJson<RoutesFile>("routes", results.get("routes")!),
      geo: {
        site: parseJson<SiteCollection>("site", results.get("site")!),
        roads: parseJson<CorridorCollection>("roads", results.get("roads")!),
        future: parseJson<CorridorCollection>("future", results.get("future")!),
        contours: parseJson<ContourCollection>("contours", results.get("contours")!),
      },
    };
    if (!Array.isArray(essentialData.data?.places) || !Array.isArray(essentialData.routes?.routes)) {
      throw new LoadError("places/routes: unexpected shape", "format");
    }
    deps.registerArchive("basemap", results.get("basemap")!);
    deps.sink.data(essentialData);
    assetBufs.clear();
    partBufs.clear();
  }

  function start(): void {
    if (state === "loading" || state === "ready" || state === "unsupported") return;
    if (deps.webgl2 && !deps.webgl2()) {
      state = "unsupported";
      lastError = "WebGL 2 is not available";
      deps.sink.error(LOADER_COPY.webgl);
      return;
    }
    state = "loading";
    lastError = null;
    deps.sink.error(null);
    runEssential().then(
      () => {
        state = "ready";
        deps.sink.progress(meter.complete(), meter.total, undefined);
        deps.sink.essential();
        resolveEssential();
        if (!persisted) {
          persisted = true;
          try {
            deps.persist?.();
          } catch {
            /* best effort */
          }
        }
        if (manifest) void pruneOldCaches(manifest.version);
      },
      (e: unknown) => {
        state = "error";
        lastError = e instanceof Error ? e.message : String(e);
        warn("essential set failed:", lastError);
        flushNow();
        deps.sink.error(calmMessage(e));
      },
    );
  }

  /* ── Deferred (DEM) ── */

  let demRetries = 0;
  let demRetryTimer: ReturnType<typeof setTimeout> | undefined;
  let demErrorShown = false;

  function loadDeferred(): void {
    if (demState === "loading" || demState === "ready") return;
    demState = "loading";
    clearTimeout(demRetryTimer);
    void (async () => {
      await essential;
      const m = manifest!;
      const a = m.assets.dem;
      if (!a) {
        demState = "error";
        warn("manifest has no deferred DEM; hillshade stays off");
        return;
      }
      demMeter.total = a.bytes;
      const ac = new AbortController();
      const buf = await loadAsset(m.version, a, demMeter, scheduleDemFlush, ac.signal);
      deps.registerArchive("dem", buf);
      assetBufs.delete("dem");
      demState = "ready";
      deps.sink.deferredProgress?.(demMeter.complete(), demMeter.total, "dem");
      deps.sink.demReady();
      resolveDem();
    })().catch((e: unknown) => {
      demState = "error";
      warn("DEM failed:", e instanceof Error ? e.message : e);
      if (!demErrorShown) {
        demErrorShown = true;
        deps.sink.deferredError?.(LOADER_COPY.generic);
      }
      // Non-essential: the map works without hillshade. Try again a few times, calmly.
      if (demRetries < 3) {
        demRetries++;
        demRetryTimer = setTimeout(loadDeferred, 15_000 * demRetries);
      }
    });
  }

  function status(): PreloadStatus {
    return {
      state,
      dem: demState,
      loaded: Math.min(meter.total, meter.value()),
      total: meter.total,
      version: manifest?.version ?? null,
      lastError,
      sources: { ...sources },
      warm: Object.fromEntries((warmRuns ?? []).map((w) => [w.item.url, { state: w.state, counted: w.counted }])),
    };
  }

  function warm(items: readonly WarmItem[]): void {
    if (warmRuns) return; // the first run has already started the set
    warmList = [...items];
  }

  return { start, essential, loadDeferred, dem, status, warm };
}

/* ─── The app's preloader ────────────────────────────────────────────────── */

let webgl2Result: boolean | null = null;
/** WebGL 2 available? Memoised; one throwaway context. */
export function hasWebGL2(): boolean {
  if (webgl2Result !== null) return webgl2Result;
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl2");
    webgl2Result = !!gl;
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
  } catch {
    webgl2Result = false;
  }
  return webgl2Result;
}

function isKiosk(): boolean {
  try {
    return getApp().kiosk || new URLSearchParams(location.search).get("kiosk") === "1";
  } catch {
    return false;
  }
}

/**
 * navigator.storage.persist() only where it cannot interrupt: the kiosk (offline matters there), and
 * Chromium, which grants or denies silently. Firefox would show a permission prompt on a first visit.
 */
function persistQuietly(): void {
  const nav = navigator as Navigator & { userAgentData?: unknown };
  if (!nav.storage?.persist) return;
  if (!isKiosk() && !nav.userAgentData) return;
  void nav.storage.persisted?.().then((already) => (already ? true : nav.storage.persist())).catch(() => false);
}

const HAS_DOM = typeof window !== "undefined" && typeof document !== "undefined";

export const preload: Preloader = createPreloader({
  fetch: (url, init) => fetch(url, init),
  caches: HAS_DOM && typeof caches !== "undefined" ? caches : null,
  subtle: typeof crypto !== "undefined" && crypto.subtle ? crypto.subtle : null,
  schedule: (cb) => {
    ticker.add(() => {
      cb();
      return false;
    });
  },
  sink: {
    progress: (loaded, total, asset) => {
      actions.setLoadProgress(loaded, total);
      bus.emit("preload:progress", { loaded, total, asset });
    },
    error: (msg) => actions.setLoadError(msg),
    data: (d) => actions.setData(d),
    essential: () => bus.emit("preload:essential"),
    deferredProgress: (loaded, total, asset) => bus.emit("preload:progress", { loaded, total, asset }),
    demReady: () => {
      actions.setDemReady(true);
      bus.emit("dem:ready");
    },
    deferredError: (message) => {
      if (getApp().phase === "attract") return; // the kiosk loop stays quiet; the switch says "Arrives after you enter"
      bus.emit("announce", { text: message });
      // The card's toast host is always mounted; loaded lazily so the loader chunk stays small.
      void import("@/features/card/Toast").then((m) => m.showToast({ id: "load", text: message, icon: "info", ms: 6000 })).catch(() => undefined);
    },
  },
  registerArchive: registerMemoryArchive,
  webgl2: HAS_DOM ? hasWebGL2 : undefined,
  offline: () => typeof navigator !== "undefined" && navigator.onLine === false,
  persist: HAS_DOM ? persistQuietly : undefined,
});
